<script setup lang="ts">
import { onMounted, computed, ref } from "vue";
import dayjs from "dayjs";

import ExecutionDetailsDTO = App.Data.Integrations.ExecutionDetailsDTO;
import { required, numericAndFloat } from "@/ui/validation/validationRules";
import { t } from "@/plugins/i18n";

function humanizeFuture(targetDate: string) {
	const now = dayjs();
	const future = dayjs(targetDate);

	const diffMs = future.diff(now); // milliseconds
	if (diffMs <= 0) return "immediately";

	const d = dayjs.duration(diffMs);

	// Build a human-readable string manually
	const parts: string[] = [];
	if (d.days() > 0) parts.push(`${d.days()} day${d.days() > 1 ? "s" : ""}`);
	if (d.hours() > 0) parts.push(`${d.hours()} hour${d.hours() > 1 ? "s" : ""}`);
	if (d.minutes() > 0)
		parts.push(`${d.minutes()} minute${d.minutes() > 1 ? "s" : ""}`);

	if (parts.length === 0) return "in less than a minute";

	const last = parts.pop();
	return parts.length > 0 ? `in ${parts.join(", ")} and ${last}` : `in ${last}`;
}

const exportDateFormat = "YYYY-MM-DD HH:mm:ss";

const upgradeProducts = [
	{
		id: "76",
		label: "P1 Business Parking",
	},
	{
		id: "79",
		label: "P2 Comfort Parking",
	},
	{
		id: "75",
		label: "P3 Long Term Parking",
	},
	{
		id: "77",
		label: "P4 Long Term Parking",
	},
	{
		id: "80",
		label: "P5 Basic Parking",
	},
];

const integrationConfigurationProperties =
	defineModel<ExecutionDetailsDTO | null>();

const inputValid = defineModel<boolean>("inputValid", {
	default: () => ref(false),
});

onMounted(() => {
	if (!integrationConfigurationProperties.value) {
		integrationConfigurationProperties.value = {
			upgrade_product: upgradeProducts[0].id,
			is_payment_required: false,
			acceptance_fee: 0.0,
			utm_campaign: "",
			description: "",
			dispatch_offer_at: dayjs(new Date()).add(1, "d").format(exportDateFormat),
		};
	}
});

const localDispatchOfferAt = computed<string>({
	get() {
		return (
			integrationConfigurationProperties.value?.dispatch_offer_at ??
			dayjs(new Date()).add(1, "d").format(exportDateFormat)
		);
	},
	set(v) {
		integrationConfigurationProperties.value.dispatch_offer_at =
			dayjs(v).format(exportDateFormat);
	},
});

const countdownToDispatch = computed(() =>
	humanizeFuture(localDispatchOfferAt.value)
);
</script>

<template>
	<v-form v-model="inputValid">
		<template v-if="integrationConfigurationProperties">
			<div class="pb-3 d-flex">
				<h6 class="text-h6 label mt-3">Upgrade to:</h6>
				<v-select
					:rules="[required(t)]"
					variant="filled"
					hide-details="auto"
					v-model="integrationConfigurationProperties.upgrade_product"
					item-value="id"
					item-title="label"
					style="text-transform: capitalize"
					:items="upgradeProducts"
				/>
			</div>
			<!--			Not sure at this point if this will come back at a later time, for now no need to show it -->
			<!--			<div class="pb-3 d-flex">-->
			<!--				<h6 class="text-h6 label mt-3">Price:</h6>-->
			<!--				<div>-->
			<!--					<v-radio-group-->
			<!--						hide-details="auto"-->
			<!--						v-model="integrationConfigurationProperties.is_payment_required"-->
			<!--						inline-->
			<!--					>-->
			<!--						<v-radio :value="false" :label="`Free of charge`"> </v-radio>-->
			<!--						<v-radio :value="true" :label="`Payment required`"></v-radio>-->
			<!--					</v-radio-group>-->
			<!--				</div>-->
			<!--			</div>-->
			<!--			<v-fade-transition>-->
			<!--				<div-->
			<!--					v-if="integrationConfigurationProperties.is_payment_required"-->
			<!--					class="pb-3 d-flex"-->
			<!--				>-->
			<!--					<h6 class="text-h6 label mt-3">Acceptance Fee:</h6>-->
			<!--					<v-text-field-->
			<!--						:rules="[required(t), numericAndFloat(t)]"-->
			<!--						hide-details="auto"-->
			<!--						variant="filled"-->
			<!--						append-inner-icon="fa: fa-light fa-euro-sign"-->
			<!--						v-model="integrationConfigurationProperties.acceptance_fee"-->
			<!--					/>-->
			<!--				</div>-->
			<!--			</v-fade-transition>-->
			<div class="pb-3 d-flex">
				<h6 class="text-h6 label mt-3">UTM Campaign:</h6>
				<v-text-field
					:rules="[required(t)]"
					placeholder="Campaign Identifier as UTM parameter"
					hide-details="auto"
					variant="filled"
					v-model="integrationConfigurationProperties.utm_campaign"
				/>
			</div>
			<div class="pb-0 d-flex">
				<h6 class="text-h6 label mt-3">Description:</h6>
				<v-textarea
					:rules="[required(t)]"
					variant="filled"
					counter
					placeholder="Enter a clear, detailed description for future reference."
					v-model="integrationConfigurationProperties.description"
					rows="3"
					hide-details="auto"
				/>
			</div>
			<v-divider class="mb-5" />
			<div class="pb-0 d-flex">
				<h6 class="text-h6 label">Send datetime:</h6>
				<div>
					<div>
						<vc-date-picker
							style="width: 350px"
							v-model="localDispatchOfferAt"
							:min-date="new Date()"
							mode="datetime"
						/>
					</div>
					<v-card-subtitle class="pa-0 mt-3">
						Offer will be dispatched to customers {{ countdownToDispatch }}
					</v-card-subtitle>
				</div>
			</div>
		</template>
	</v-form>
</template>

<style lang="scss" scoped>
.label {
	width: 250px;
}
</style>
